import React from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Button } from './ui/button';

export const WeekView = ({ 
  tasks, 
  currentWeekStart, 
  onNavigateWeek,
  getTasksByDate,
  onTaskClick
}) => {
  const dayNamesFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayNamesShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  const getWeekDays = () => {
    const days = [];
    const start = new Date(currentWeekStart);
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    
    return days;
  };

  const weekDays = getWeekDays();
  const weekEnd = new Date(currentWeekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const formatWeekRange = () => {
    const startStr = currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const weekTaskCounts = React.useMemo(() => {
    const total = tasks.filter(t => {
      const taskDate = t.due_date ? new Date(t.due_date) : null;
      return taskDate && taskDate >= currentWeekStart && taskDate <= weekEnd;
    });
    return {
      total: total.length,
      open: total.filter(t => t.status === 'open').length,
      complete: total.filter(t => t.status === 'complete').length,
    };
  }, [tasks, currentWeekStart, weekEnd]);

  return (
    <div className="space-y-4">
      {/* Week Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg sm:text-xl font-semibold text-slate-900">
          {formatWeekRange()}
        </h3>
        <div className="flex gap-1 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigateWeek(-1)}
            data-testid="prev-week"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigateWeek(0)}
            data-testid="this-week"
            className="text-xs sm:text-sm"
          >
            This Week
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigateWeek(1)}
            data-testid="next-week"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Desktop: 7-col grid */}
      <div className="hidden md:grid grid-cols-7 gap-3">
        {weekDays.map((day, idx) => {
          const dayTasks = getTasksByDate(day);
          const today = isToday(day);
          
          return (
            <div
              key={day.toISOString()}
              className={`border rounded-lg overflow-hidden ${
                today ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'
              }`}
              data-testid={`week-day-${idx}`}
            >
              <div className={`p-3 border-b ${today ? 'bg-blue-100 border-blue-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className={`text-xs font-semibold uppercase ${today ? 'text-blue-600' : 'text-slate-600'}`}>
                  {dayNamesFull[day.getDay()]}
                </div>
                <div className={`text-2xl font-bold ${today ? 'text-blue-700' : 'text-slate-900'}`}>
                  {day.getDate()}
                </div>
                {dayTasks.length > 0 && (
                  <div className="text-xs text-slate-600 mt-1">
                    {dayTasks.length} task{dayTasks.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
              <div className="p-2 space-y-2 max-h-96 overflow-y-auto">
                {dayTasks.length === 0 ? (
                  <div className="text-center py-4 text-sm text-slate-400">
                    No tasks
                  </div>
                ) : (
                  dayTasks.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => onTaskClick?.(task)}
                      className={`p-2 rounded border text-sm cursor-pointer hover:ring-1 hover:ring-blue-400 transition-all ${
                        task.status === 'open'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-green-50 border-green-200'
                      }`}
                    >
                      <div className="font-semibold text-slate-900 mb-1 text-xs">
                        {task.activity}
                      </div>
                      <div className="text-xs text-slate-600 mb-2 line-clamp-2">
                        {task.content}
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1 text-slate-500">
                          <Clock className="w-3 h-3" />
                          {task.hours_log}h
                        </div>
                        <span
                          className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                            task.status === 'open'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {task.status}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: vertical stacked cards */}
      <div className="block md:hidden space-y-2">
        {weekDays.map((day, idx) => {
          const dayTasks = getTasksByDate(day);
          const today = isToday(day);
          
          return (
            <div
              key={day.toISOString()}
              className={`border rounded-lg overflow-hidden ${
                today ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200 bg-white'
              }`}
              data-testid={`week-day-mobile-${idx}`}
            >
              <div className={`px-3 py-2 flex items-center justify-between border-b ${
                today ? 'bg-blue-100 border-blue-200' : 'bg-slate-50 border-slate-200'
              }`}>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${today ? 'text-blue-700' : 'text-slate-900'}`}>
                    {day.getDate()}
                  </span>
                  <span className={`text-sm font-semibold ${today ? 'text-blue-600' : 'text-slate-600'}`}>
                    {dayNamesShort[day.getDay()]}
                  </span>
                  {today && (
                    <span className="text-[10px] bg-blue-600 text-white rounded-full px-2 py-0.5 font-medium uppercase">
                      Today
                    </span>
                  )}
                </div>
                {dayTasks.length > 0 && (
                  <span className="text-xs bg-blue-600 text-white rounded-full px-2 py-0.5 font-medium">
                    {dayTasks.length} task{dayTasks.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {dayTasks.length === 0 ? (
                <div className="px-3 py-3 text-sm text-slate-400">No tasks</div>
              ) : (
                <div className="p-2 space-y-2">
                  {dayTasks.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => onTaskClick?.(task)}
                      className={`p-2.5 rounded-lg border cursor-pointer hover:ring-1 hover:ring-blue-400 transition-all ${
                        task.status === 'open'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-green-50 border-green-200'
                      }`}
                    >
                      <div className="font-semibold text-sm text-slate-900 mb-1">{task.activity}</div>
                      {task.content && (
                        <div className="text-xs text-slate-600 mb-2 line-clamp-2">{task.content}</div>
                      )}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1 text-slate-500">
                          <Clock className="w-3 h-3" />
                          {task.hours_log || 0}h
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                          task.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-600 pt-4 border-t border-slate-200">
        <div>
          <span className="font-semibold text-slate-900">{weekTaskCounts.total}</span> tasks this week
        </div>
        <div>
          <span className="font-semibold text-blue-600">{weekTaskCounts.open}</span> open
        </div>
        <div>
          <span className="font-semibold text-green-600">{weekTaskCounts.complete}</span> complete
        </div>
      </div>
    </div>
  );
};
